import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { fmtDateTime } from "@/lib/format";
import { ArrowRight } from "lucide-react";

const titleize = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

// Enterprise-style audit detail: who/when/where + a compact Old→New diff + the object snapshot
// captured at event time. No full JSON is stored — everything shown comes from the log record.
export function LogDetailDrawer({ log, open, onOpenChange }) {
  if (!log) return null;
  const changes = log.changes || [];
  const snapshot = log.snapshot || {};
  const meta = [
    ["Changed by", log.actor],
    ["When", fmtDateTime(log.created_at) + " UTC"],
    ["IP address", log.ip],
    ["User agent", log.user_agent],
    ["Tenant", log.tenant_id],
    ["Category", log.category],
    ["Action", log.action],
    ["Object", log.entity_label],
  ].filter(([, v]) => v);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto border-white/[0.08] bg-[#141417] sm:max-w-md" data-testid="log-detail-drawer">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <SheetTitle className="text-foreground">{log.entity_label || titleize(log.category) + " event"}</SheetTitle>
          </div>
          <p className="text-sm text-muted-foreground">{log.message}</p>
          <SheetDescription className="sr-only">Audit event details for {log.entity_label || log.action}</SheetDescription>
          <div className="pt-1"><span className="rounded bg-white/[0.06] px-2 py-0.5 font-mono text-xs text-muted-foreground">{log.action}</span></div>
        </SheetHeader>

        <div className="mt-5 space-y-6">
          {changes.length > 0 && (
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">What changed</h4>
              <div className="space-y-2">
                {changes.map((c, i) => (
                  <div key={i} className="rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-3" data-testid={`log-change-${c.field}`}>
                    <div className="mb-1.5 text-xs font-medium text-foreground">{titleize(c.field)}</div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="rounded bg-[#EF4444]/[0.1] px-2 py-0.5 font-mono text-xs text-[#fca5a5] line-through">{fmtVal(c.old)}</span>
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="rounded bg-[#22C55E]/[0.1] px-2 py-0.5 font-mono text-xs text-[#86efac]">{fmtVal(c.new)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Object.keys(snapshot).length > 0 && (
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">Object</h4>
              <div className="divide-y divide-white/[0.05] rounded-lg border border-white/[0.06] bg-[#0A0A0A]">
                {Object.entries(snapshot).map(([k, v]) => (
                  <div key={k} className="flex items-start justify-between gap-4 px-3 py-2 text-sm">
                    <span className="text-muted-foreground/70">{titleize(k)}</span>
                    <span className="max-w-[60%] break-words text-right text-foreground/90">{fmtVal(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">Event metadata</h4>
            <div className="divide-y divide-white/[0.05] rounded-lg border border-white/[0.06] bg-[#0A0A0A]">
              {meta.map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-4 px-3 py-2 text-sm">
                  <span className="text-muted-foreground/70">{k}</span>
                  <span className="max-w-[65%] break-words text-right font-mono text-xs text-foreground/80">{fmtVal(v)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
